// designTokens.ts — jaettujen ulkoasutokenien luku webiin.
//
// MIKSI: sama operaattorikuori piirretään kahdesti — webissä (tämä koodi) ja
// Swift-natiivisovelluksessa. Jos värit ja mitat asuvat molemmissa omina
// kovakoodattuina literaaleina, ne ajautuvat vääjäämättä erilleen ja jokainen
// ulkoasumuutos pitää tehdä kahdesti. Siksi arvot luetaan shared/-kansion
// yhdestä lähteestä, jota myös backendin /api/v1/ui/theme tarjoilee natiiville.
//
// MIKSI BUILD-AIKAINEN: JSON importoidaan bundleen käännösaikana eikä haeta
// verkosta. Näin sivunlatauksessa ei ole verkkoriippuvuutta eikä ulkoasu
// välähdä väärillä väreillä ennen kuin teema saapuu. Endpointit ovat natiivia
// varten, jonka pitää saada teema jo kirjautumisruudulle.
import tokens from '../../../shared/design-tokens.json';

export type AlphaColor = { value: string; alpha: number };

export const TOKENS = tokens;

/**
 * #rrggbb + alfa → css-rgba(). Tokenilähteessä ei ole alfaa hexissä (natiivi
 * lukee samat arvot eikä osaa 8-merkkistä hexiä yhtä suoraviivaisesti), joten
 * läpikuultavat pinnat esitetään { value, alpha } -pareina.
 */
export function rgba(c: AlphaColor): string {
  const hex = c.value.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${c.alpha})`;
}

// CSS:n geneeriset ja järjestelmäperheet EIVÄT saa lainausmerkkejä; nimetyt
// fonttiperheet saavat. Sääntö pitää tuotetun fonttipinon merkilleen samana
// kuin webissä ennen tokenointia oli.
const KEYWORD_FAMILIES = new Set([
  'system-ui',
  'ui-sans-serif',
  'ui-monospace',
  '-apple-system',
  'BlinkMacSystemFont',
  'sans-serif',
  'serif',
  'monospace',
  'cursive',
  'fantasy',
  'inherit',
]);

/** ["Inter","system-ui","sans-serif"] → "'Inter', system-ui, sans-serif" */
export function cssFontStack(families: readonly string[]): string {
  return families.map(f => (KEYWORD_FAMILIES.has(f) ? f : `'${f}'`)).join(', ');
}

export const FONT_FAMILY = cssFontStack(TOKENS.font.family);
export const FONT_FAMILY_NARROW = cssFontStack(TOKENS.font.familyNarrow);

/**
 * Kuoren tausta: kolmiportainen pystygradientti. Portaiden määrä luetaan
 * tokenista, joten neljäs sävy ei vaadi koodimuutosta.
 */
export function shellBackgroundGradient(): string {
  const stops = TOKENS.color.backgroundGradient;
  const steps = stops
    .map((c, i) => `${c} ${Math.round((i / (stops.length - 1)) * 100)}%`)
    .join(', ');
  return `linear-gradient(180deg, ${steps})`;
}

/**
 * Kiinteä korkeus + laitteen turva-alue. Sama laskutapa ylä- ja alapalkille:
 * env() lisätään TÄSMÄLLEEN kerran (box-sizing: border-box hoitaa loput) —
 * ks. OperatorShell.tsx:n kommentit 28.8.2026.
 */
export function withSafeArea(px: number, inset: 'top' | 'bottom'): string {
  return `calc(${px}px + env(safe-area-inset-${inset}, 0px))`;
}
